import { Lang } from '@/lib/i18n';
import type { DeviceType } from '@/data/types';

// A device's `type` field is not one shape. The legacy Excel import wrote the
// German column header as a label ("Schaum Aufladedruck") while devices created
// or edited in the app store the device-type id ("dt-1"). Both land in the same
// field and in customer.deviceCounts keys, so anything that displays a type has
// to accept either and resolve it — otherwise the technician sees a bare
// "dt-1", which means nothing on site (client #80).

/** Canonical device-type id for a stored value, or the value if unknown. */
export function normalizeDeviceTypeKey(
  value: string,
  deviceTypes: DeviceType[]
): string {
  const byId = deviceTypes.find(dt => dt.id === value);
  if (byId) return byId.id;
  const byName = deviceTypes.find(
    dt => dt.nameDe === value || dt.nameEn === value
  );
  return byName ? byName.id : value;
}

/**
 * Collapse a deviceCounts map onto canonical device-type ids, summing the
 * buckets that resolve to the same type. Necessary because the same type can
 * appear under two keys — "dt-5" from the app and "Schaum Dauerdruck" from the
 * import — which would otherwise render as two identically-labelled columns.
 */
export function resolveDeviceCounts(
  counts: Record<string, number> | undefined,
  deviceTypes: DeviceType[]
): Record<string, number> {
  const out: Record<string, number> = {};
  Object.entries(counts ?? {}).forEach(([k, v]) => {
    const key = normalizeDeviceTypeKey(k, deviceTypes);
    out[key] = (out[key] ?? 0) + v;
  });
  return out;
}

/**
 * Human label for a stored device-type value. Falls back to the raw value so an
 * orphaned id still renders something rather than blanking the column.
 */
export function deviceTypeLabel(
  value: string | undefined,
  deviceTypes: DeviceType[],
  lang: Lang = Lang.DE
): string {
  if (!value) return '';
  const dt = deviceTypes.find(
    x => x.id === value || x.nameDe === value || x.nameEn === value
  );
  if (!dt) return value;
  return (lang === Lang.DE ? dt.nameDe : dt.nameEn) || dt.nameDe || value;
}
